import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Button, { ButtonProps } from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiKey } from '../../contexts/ApiKeyContext';
import { useSelectedTournament } from '../../contexts/SelectedTournamentContext';
import { Match, Tournament } from '../../models/Tournament';
import { fetchEvents, updateScore } from '../../services/MatchService';
import { endMatch, fetchTournament } from '../../services/TournamentService';
import { ConfirmDialog } from '../../utils/ConfirmDialog';
import { getDateFromDateTime, getShortDateFormatWithDayOfWeek, getShortDateTimeFormat } from '../../utils/Dates';
import { getImageUrl } from '../../utils/Url';
import { ScoreUpdateDialog } from './ScoreUpdateDialog';

const buttonProps: ButtonProps = {
  sx: { marginTop: 0.5 },
  variant: 'outlined',
  fullWidth: true,
  size: 'small',
};

export const Schedule = () => {
  const { tournamentId, phaseId, teamId } = useParams<{ tournamentId: string; phaseId: string; teamId: string }>();
  const { apiKey, isReferee, isAdmin } = useApiKey();
  const { setSelectedTournament } = useSelectedTournament();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState<Tournament>();
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | undefined>(phaseId ?? undefined);
  const [soccerTeams, setSoccerTeams] = useState<{ id: number; logo: string; name: string }[] | any[]>([]);

  //filters
  const [showUnfinishedMatches, setShowUnfinishedMatches] = useState<boolean>(false);
  const [showTodayMatches, setShowTodayMatches] = useState<boolean>(false);
  const [selectedTeamId, setSelectedTeamId] = useState<number | ''>(teamId ? Number(teamId) : '');
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);

  const [selectedMatch, setSelectedMatch] = useState<{ match: Match; operation: 'end' | 'scoreUpdate' }>();

  useEffect(() => {
    if (tournamentId) {
      fetchTournament(apiKey, tournamentId).then((result) => {
        setTournament(result);
        setSelectedTournament(result);
        if (selectedPhaseId === undefined) {
          setSelectedPhaseId(result.phases[0].id.toString());
        }
        const uniqueTeamIds = new Set<number>();
        const teams: { id: number; logo: string; name: string }[] = [];
        result.phases.forEach((phase) => {
          phase.groups.forEach((group) => {
            group.groupSoccerTeams?.forEach((gst) => {
              if (gst.soccerTeam && !uniqueTeamIds.has(gst.soccerTeam.id)) {
                uniqueTeamIds.add(gst.soccerTeam.id);
                teams.push(gst.soccerTeam);
              }
            });
          });
        });
        teams.sort((a, b) => a.name.localeCompare(b.name, 'pl', { sensitivity: 'base' }));
        setSoccerTeams(teams);
      });
    }
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedPhaseId(newValue);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'unfinished' | 'today') => {
    if (type === 'unfinished') {
      setShowUnfinishedMatches(event.target.checked);
    } else if (type === 'today') {
      setShowTodayMatches(event.target.checked);
    }
  };

  const toggleFiltersVisibility = () => {
    setFiltersVisible((prev) => !prev);
  };

  const filteredMatches = useMemo(() => {
    const matchesToFilter =
      tournament?.phases?.find((phase) => phase.id.toString() === selectedPhaseId)?.groups.flatMap((group) => group.matches) ||
      [];

    return matchesToFilter.filter((match) => {
      const isToday = dayjs(match.matchDate).isSame(dayjs(), 'day');
      const isUnfinished = !match.isFinished;
      const involvesSelectedTeam =
        selectedTeamId === '' || match.soccerTeam1Id === selectedTeamId || match.soccerTeam2Id === selectedTeamId;

      if (showUnfinishedMatches && showTodayMatches) {
        return isToday && isUnfinished && involvesSelectedTeam;
      }
      if (showUnfinishedMatches) {
        return isUnfinished && involvesSelectedTeam;
      }
      if (showTodayMatches) {
        return isToday && involvesSelectedTeam;
      }
      return involvesSelectedTeam;
    });
  }, [tournament, selectedPhaseId, showUnfinishedMatches, showTodayMatches, selectedTeamId]);

  const getSoccerTeamLogo = (soccerTeamId?: number) => {
    const team = soccerTeams.find((team) => team.id === soccerTeamId);
    if (team === undefined || !team.logo) {
      return <Typography variant="h4">⚽</Typography>;
    }
    return <img width={'45'} height={'45'} src={getImageUrl(team.logo)} alt={'Logo zespołu ' + team.name} />;
  };

  const getSoccerTeamLabel = (match: Match, formulaIndex: 0 | 1, soccerTeamId?: number) => {
    if (soccerTeamId !== undefined && soccerTeamId !== 0 && soccerTeamId !== -1) {
      return <Typography sx={{ display: 'block', wordBreak: 'break-word' }}>{getSoccerTeamName(soccerTeamId)}</Typography>;
    } else if (match.formula !== undefined && match.formula !== null) {
      return (
        <Typography sx={{ display: 'block', wordBreak: 'break-word' }}>{match.formula?.split(':')[formulaIndex]}</Typography>
      );
    } else return <Typography sx={{ display: 'block', wordBreak: 'break-word' }}>{'-'}</Typography>;
  };

  const getSoccerTeamName = (soccerTeamId?: number): string => {
    const team = soccerTeams.find((team) => team.id === soccerTeamId);
    if (team === undefined) {
      return '-';
    }
    return team.name;
  };

  const goToRefereeView = (matchId: number) => {
    navigate(`/match/${matchId}/referee`);
  };

  const updateTournamentMatch = (tournament: Tournament, matchId: number, updater: (match: Match) => void): Tournament => {
    const updatedTournament = { ...tournament };

    const phaseIndex = updatedTournament.phases.findIndex((phase) =>
      phase.groups.some((group) => group.matches.some((match) => match.id === matchId)),
    );

    if (phaseIndex !== -1) {
      const groupIndex = updatedTournament.phases[phaseIndex].groups.findIndex((group) =>
        group.matches.some((match) => match.id === matchId),
      );

      if (groupIndex !== -1) {
        const matchIndex = updatedTournament.phases[phaseIndex].groups[groupIndex].matches.findIndex(
          (match) => match.id === matchId,
        );

        if (matchIndex !== -1) {
          const matchToUpdate = updatedTournament.phases[phaseIndex].groups[groupIndex].matches[matchIndex];

          updater(matchToUpdate);
        }
      }
    }
    return updatedTournament;
  };

  const handleEndMatchClick = (match: Match) => {
    setSelectedMatch({ match, operation: 'end' });
  };

  const handleConfirmEndMatch = () => {
    if (tournament && selectedMatch) {
      endMatch(apiKey, tournament.id, selectedMatch.match.id!).then(() => {
        const updatedTournament = updateTournamentMatch(tournament, selectedMatch.match.id!, (match) => {
          match.isFinished = true;
        });
        setTournament(updatedTournament);
      });
    }
    setSelectedMatch(undefined);
  };

  const handleCancelEndMatch = () => {
    setSelectedMatch(undefined);
  };

  const handleUpdateScoreMatchClick = (match: Match) => {
    setSelectedMatch({ match, operation: 'scoreUpdate' });
  };

  const handleUpdateScoreCancel = () => {
    setSelectedMatch(undefined);
  };

  const handleUpdateScoreConfirm = (team1Goals: number, team2Goals: number) => {
    if (tournament && selectedMatch) {
      updateScore(apiKey, selectedMatch.match.id!, team1Goals, team2Goals)
        .then((updatedMatch) => {
          const updatedTournament = updateTournamentMatch(tournament, selectedMatch.match.id!, (match) => {
            match.soccerTeam1Goals = team1Goals;
            match.soccerTeam2Goals = team2Goals;
          });
          setTournament(updatedTournament);
        })
        .finally(() => setSelectedMatch(undefined));
    }
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (showUnfinishedMatches) count++;
    if (showTodayMatches) count++;
    if (selectedTeamId !== '') count++;
    return count;
  }, [showUnfinishedMatches, showTodayMatches, selectedTeamId]);

  const handleClearFilters = () => {
    setShowUnfinishedMatches(false);
    setShowTodayMatches(false);
    setSelectedTeamId('');
  };

  return (
    <>
      {tournament && tournament.phases.length > 0 && selectedPhaseId !== undefined && (
        <TabContext value={selectedPhaseId}>
          <TabList onChange={handleChange} aria-label="Fazy" variant="fullWidth">
            {tournament.phases.map((phase) => (
              <Tab key={'tab' + phase.id} label={phase.name} value={phase.id.toString()} />
            ))}
          </TabList>
          {tournament.phases.map((phase) => (
            <TabPanel key={'tabPanel' + phase.id} value={phase.id.toString()} sx={{ padding: 1 }}>
              <Grid2 container justifyContent="center" alignItems="center">
                <IconButton onClick={toggleFiltersVisibility}>
                  {filtersVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography>
                  Filtry {activeFiltersCount > 0 && <span style={{ fontWeight: 'bold' }}>({activeFiltersCount})</span>}
                </Typography>
                {activeFiltersCount > 0 && (
                  <IconButton onClick={handleClearFilters}>
                    <FilterAltOffIcon />
                  </IconButton>
                )}
                <Grid2 size={{ xs: 12 }}>
                  <Collapse in={filtersVisible}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showUnfinishedMatches}
                          onChange={(event) => handleSwitchChange(event, 'unfinished')}
                          color="primary"
                        />
                      }
                      label={`Nie zakończone mecze`}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showTodayMatches}
                          onChange={(event) => handleSwitchChange(event, 'today')}
                          color="primary"
                        />
                      }
                      label={`Dzisiejsze mecze`}
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel id="team-filter-label">Drużyna</InputLabel>
                      <Select
                        labelId="team-filter-label"
                        value={selectedTeamId}
                        onChange={(event) => setSelectedTeamId(event.target.value as number | '')}
                        label="Zespół"
                      >
                        <MenuItem value="">Wszystkie drużyny</MenuItem>
                        {soccerTeams.map((team) => (
                          <MenuItem key={team.id} value={team.id}>
                            {team.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Collapse>
                </Grid2>
              </Grid2>
              <Grid2 container spacing={3} justifyContent={'center'}>
                {phase.groups.map((group) => {
                  let previousDate: string | null = null;
                  return (
                    <Grid2 size={{ xs: 12, md: 8 }} key={group.id} justifyContent={'center'}>
                      <TableContainer component={Paper} elevation={2}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell align="center" width={'33%'}>
                                Drużyna 1
                              </TableCell>
                              <TableCell align="center" width={'34%'}>
                                <Typography>{group.name}</Typography>
                              </TableCell>
                              <TableCell align="center" width={'33%'}>
                                Drużyna 2
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredMatches
                              .filter((match) => match.groupId === group.id)
                              .map((match, index) => {
                                const currentDate = getDateFromDateTime(match.matchDate!);
                                const showDateRow = currentDate !== previousDate;
                                previousDate = currentDate; // Update the previous date for the next iteration
                                return (
                                  <React.Fragment key={match.id}>
                                    {showDateRow && (
                                      <TableRow>
                                        <TableCell colSpan={3} align="center" className="row-separator">
                                          <Typography>
                                            {getShortDateFormatWithDayOfWeek(match.matchDate!).toUpperCase()}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                    <TableRow hover>
                                      <TableCell align="center">
                                        {getSoccerTeamLogo(match.soccerTeam1Id)}
                                        {getSoccerTeamLabel(match, 0, match.soccerTeam1Id)}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography variant="h5">
                                          {match.soccerTeam1Goals ?? '-'} : {match.soccerTeam2Goals ?? '-'}
                                        </Typography>
                                        <Typography variant="body2">{match.venue}</Typography>
                                        <Typography variant="body2">{getShortDateTimeFormat(match.matchDate!)}</Typography>
                                        {match.isFinished && (
                                          <Typography
                                            variant="body2"
                                            color={'crimson'}
                                            onClick={() => {
                                              fetchEvents(apiKey, match.id).then(console.log);
                                            }}
                                          >
                                            Zakończony
                                          </Typography>
                                        )}
                                        {(isAdmin || isReferee) && !match.isFinished && (
                                          <Button {...buttonProps} color="primary" onClick={() => goToRefereeView(match.id!)}>
                                            Sędziuj
                                          </Button>
                                        )}
                                        {(isAdmin || isReferee) && (
                                          <>
                                            <Button
                                              {...buttonProps}
                                              color="secondary"
                                              onClick={() => handleUpdateScoreMatchClick(match)}
                                            >
                                              Edytuj
                                            </Button>
                                            <Button {...buttonProps} color="error" onClick={() => handleEndMatchClick(match)}>
                                              Zakończ
                                            </Button>
                                          </>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {getSoccerTeamLogo(match.soccerTeam2Id)}
                                        {getSoccerTeamLabel(match, 1, match.soccerTeam2Id)}
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid2>
                  );
                })}
              </Grid2>
            </TabPanel>
          ))}
        </TabContext>
      )}
      {selectedMatch && selectedMatch.operation === 'end' && (
        <ConfirmDialog
          title="Zakończenie meczu"
          content={
            <>
              <Typography>Czy chcesz zakończyć mecz:</Typography>
              <Typography>
                {getSoccerTeamName(selectedMatch.match.soccerTeam1Id)} vs. {getSoccerTeamName(selectedMatch.match.soccerTeam2Id)}
              </Typography>
            </>
          }
          onClose={handleCancelEndMatch}
          onConfirm={handleConfirmEndMatch}
        />
      )}
      {selectedMatch && selectedMatch.operation === 'scoreUpdate' && (
        <ScoreUpdateDialog
          open={true}
          team1Name={getSoccerTeamName(selectedMatch.match.soccerTeam1Id)}
          team2Name={getSoccerTeamName(selectedMatch.match.soccerTeam2Id)}
          currentTeam1Goals={selectedMatch.match.soccerTeam1Goals}
          currentTeam2Goals={selectedMatch.match.soccerTeam2Goals}
          onClose={handleUpdateScoreCancel}
          onConfirm={handleUpdateScoreConfirm}
        />
      )}
    </>
  );
};
